import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Sparkles, ArrowRight, X, Loader2, TrendingUp, ArrowUpRight } from 'lucide-react';
import { trackEvent } from '../lib/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SUGGESTED_BRANDS = ['Patagonia', 'Aesop', 'A24', 'Rhode', 'On Running', 'Aperol', 'Notion'];

const PROGRESS_STEPS = [
  'Analyzing category signal',
  'Reading audience truth',
  'Structuring co-brand map',
  'Drafting revenue thesis',
  'Rendering campaign visual',
];

const RELATED_CASES = {
  nike:       { title: 'Nike · Culture',       tag: 'CO-BRAND · ATHLETE PARTNERSHIPS' },
  mercedes:   { title: 'Mercedes-Benz · Film', tag: 'BRAND FILM · IDENTITY' },
  comisario:  { title: 'Comisario · Spirits',  tag: 'CATEGORY LAUNCH · SPIRITS' },
  'jones':     { title: 'Jones Soda · Drops',  tag: 'CO-BRAND · LIMITED EDITIONS' },
  delta:      { title: 'Delta · Co-Brand',     tag: 'BRAND · CO-BRAND' },
};

function useTypewriter(text, speedMs = 12) {
  const [out, setOut] = useState('');
  useEffect(() => {
    if (!text) { setOut(''); return; }
    setOut('');
    let i = 0;
    const t = setInterval(() => {
      i += 1;
      setOut(text.slice(0, i));
      if (i >= text.length) clearInterval(t);
    }, speedMs);
    return () => clearInterval(t);
  }, [text, speedMs]);
  return out;
}

function ScoreGauge({ label, value, color, delay = 0 }) {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    const t = setTimeout(() => {
      const dur = 900;
      const start = performance.now();
      const tick = (now) => {
        const p = Math.min(1, (now - start) / dur);
        // ease-out cubic
        const eased = 1 - Math.pow(1 - p, 3);
        setDisplay(Math.round(eased * value));
        if (p < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    }, delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  const pct = Math.max(0, Math.min(100, display));
  return (
    <div className="flex-1">
      <p className="text-[10px] tracking-[0.3em] text-white/55 mb-2">{label}</p>
      <div className="flex items-baseline gap-2 mb-2">
        <span className="font-display text-4xl md:text-5xl leading-none" style={{ color }}>{display}</span>
        <span className="text-white/40 text-sm">/ 100</span>
      </div>
      <div className="relative h-[3px] bg-white/10 overflow-hidden">
        <div className="absolute inset-y-0 left-0 transition-[width] duration-100" style={{ width: `${pct}%`, background: color }} />
      </div>
    </div>
  );
}

export default function BrandPreview() {
  const [brand, setBrand] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [progressIdx, setProgressIdx] = useState(0);
  const [visual, setVisual] = useState(null);
  const [visualLoading, setVisualLoading] = useState(false);
  const [elapsedSec, setElapsedSec] = useState(0);
  const [phase, setPhase] = useState(null); // 'generating_thesis' | 'generating_visual' | 'done' | 'error'
  const progressTimersRef = useRef([]);
  const elapsedTimerRef = useRef(null);

  const typedThesis = useTypewriter(result?.thesis || '');

  const clearProgressTimers = () => {
    progressTimersRef.current.forEach((t) => clearTimeout(t));
    progressTimersRef.current = [];
    if (elapsedTimerRef.current) {
      clearInterval(elapsedTimerRef.current);
      elapsedTimerRef.current = null;
    }
  };

  const startProgressTicker = () => {
    setProgressIdx(0);
    setElapsedSec(0);
    clearProgressTimers();
    // Advance through steps 0→1→2→3 while GPT-5 runs
    [1200, 2500, 4000, 6000].forEach((ms, i) => {
      const t = setTimeout(() => setProgressIdx((p) => Math.max(p, i + 1)), ms);
      progressTimersRef.current.push(t);
    });
    elapsedTimerRef.current = setInterval(() => setElapsedSec((s) => s + 1), 1000);
  };

  const submit = async (e) => {
    e?.preventDefault();
    return runPreview(brand);
  };

  const runPreview = async (rawBrand) => {
    const b = (rawBrand || '').trim();
    if (!b || loading) return;
    setBrand(b);
    setLoading(true);
    setError(null);
    setResult(null);
    setVisual(null);
    setPhase('generating_thesis');
    startProgressTicker();
    trackEvent('brand_preview_start', { event_category: 'engagement', brand: b });
    try {
      // 1) Kick off the job (returns immediately with a job_id)
      const startRes = await axios.post(`${API}/brand/preview`, { brand: b }, { timeout: 15000 });
      const jobId = startRes.data?.job_id;
      if (!jobId) throw new Error('No job_id returned');

      // 2) Poll for status. GPT-5 + Nano Banana usually finish in 40–70s.
      const started = Date.now();
      const MAX_MS = 180000; // 3 min hard cap
      let thesisSet = false;
      // Keep the visual loader on until we get an image OR the job finishes
      setVisualLoading(true);

      while (Date.now() - started < MAX_MS) {
        await new Promise((r) => setTimeout(r, 2000));
        let s;
        try {
          s = await axios.get(`${API}/brand/preview/status/${jobId}`, { timeout: 10000 });
        } catch {
          continue; // transient — keep polling
        }
        const { status, result: r, image_data_url, error: jobErr } = s.data || {};

        if (r && !thesisSet) {
          setResult(r);
          setProgressIdx(4);
          thesisSet = true;
          try {
            localStorage.setItem('smg_brand_preview_lead', JSON.stringify({
              brand: r.brand,
              category: r.category,
              thesis: r.thesis,
              tagline: r.tagline,
              ts: Date.now(),
            }));
          } catch { /* noop */ }
        }
        if (image_data_url) setVisual(image_data_url);

        if (status === 'done') {
          setVisualLoading(false);
          return;
        }
        if (status === 'error') {
          throw new Error(jobErr || 'Preview generation failed');
        }
      }
      throw new Error('Preview timed out. Please try again.');
    } catch (err) {
      setError(
        err?.response?.data?.detail?.toString() ||
          err?.message ||
          'Preview is briefly offline. Try again in a moment.'
      );
    } finally {
      setLoading(false);
      setVisualLoading(false);
      clearProgressTimers();
    }
  };

  const reset = () => {
    clearProgressTimers();
    setResult(null);
    setError(null);
    setBrand('');
    setVisual(null);
    setProgressIdx(0);
  };

  const pickBrand = (b) => {
    runPreview(b);
  };

  useEffect(() => () => clearProgressTimers(), []);

  const related = result?.related_case_slug && RELATED_CASES[result.related_case_slug];

  return (
    <div className="w-full" data-testid="brand-preview-widget">
      {!result && !loading && (
        <form onSubmit={submit} className="relative">
          <label className="text-[10px] tracking-[0.35em] text-[#FF3D2E] mb-3 block">
            — TEST YOUR BRAND WITH SMG
          </label>
          <div className="relative flex flex-col sm:flex-row sm:items-center border border-white/25 focus-within:border-white/60 transition-colors bg-black/40 backdrop-blur">
            <input
              type="text"
              value={brand}
              onChange={(e) => setBrand(e.target.value)}
              placeholder="Type a brand name — Nike, Patagonia, your own…"
              disabled={loading}
              className="flex-1 bg-transparent px-5 py-4 text-white placeholder:text-white/35 focus:outline-none text-base"
              data-testid="brand-preview-input"
            />
            <button
              type="submit"
              disabled={loading || !brand.trim()}
              className="group inline-flex items-center justify-center gap-2 px-5 md:px-6 py-4 bg-[#FF3D2E] hover:bg-[#ff5446] disabled:opacity-40 disabled:hover:bg-[#FF3D2E] text-white text-[11px] tracking-[0.25em] transition-colors border-t sm:border-t-0 border-white/10"
              data-testid="brand-preview-submit"
            >
              <Sparkles className="w-4 h-4 group-hover:rotate-12 transition-transform" />
              RUN THE PREVIEW
              <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
          <p className="mt-3 text-[11px] text-white/45 leading-relaxed max-w-md">
            Live GPT-5 thesis + Nano Banana visual mockup. Runs in ~60 seconds. No signup.
          </p>

          {/* Suggested brand chips */}
          <div className="mt-5 flex flex-wrap items-center gap-2" data-testid="brand-preview-chips">
            <span className="text-[10px] tracking-[0.3em] text-white/40 mr-1">TRY:</span>
            {SUGGESTED_BRANDS.map((b) => (
              <button
                key={b}
                type="button"
                onClick={() => pickBrand(b)}
                className="text-[11px] tracking-[0.15em] text-white/70 hover:text-white border border-white/15 hover:border-[#FF3D2E] px-3 py-1.5 transition-colors"
                data-testid={`brand-chip-${b.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {b}
              </button>
            ))}
          </div>

          {error && (
            <p className="mt-3 text-[12px] text-[#FF3D2E]" data-testid="brand-preview-error">
              {error}
            </p>
          )}
        </form>
      )}

      {loading && (
        <div className="border border-white/15 bg-black/60 backdrop-blur p-6 md:p-8 animate-[fadeUp_400ms_ease-out]" data-testid="brand-preview-loading">
          <div className="flex items-center justify-between gap-3 mb-6">
            <div className="flex items-center gap-3">
              <Loader2 className="w-5 h-5 animate-spin text-[#FF3D2E]" />
              <p className="text-[10px] tracking-[0.35em] text-[#FF3D2E]">
                SMG · STRATEGIC INTELLIGENCE ENGINE
              </p>
            </div>
            <div className="flex items-baseline gap-2 tabular-nums">
              <span className="font-display text-2xl md:text-3xl text-white leading-none">
                {String(Math.floor(elapsedSec / 60)).padStart(2, '0')}:{String(elapsedSec % 60).padStart(2, '0')}
              </span>
              <span className="text-[10px] tracking-[0.3em] text-white/40">ELAPSED</span>
            </div>
          </div>
          <p className="text-white text-xl md:text-2xl font-display tracking-tight mb-3">
            Building a bespoke brief for <span className="text-[#FF3D2E]">{brand}</span>…
          </p>
          <p className="text-white/60 text-sm mb-5 min-h-[1.25rem]">
            {phase === 'generating_visual'
              ? 'Rendering your bespoke campaign visual — this is the final step.'
              : elapsedSec > 40
              ? 'Still working. GPT-5 is composing the moves and revenue thesis…'
              : elapsedSec > 20
              ? 'Reading category signal and audience truth…'
              : 'Analyzing brand position and competitive white space…'}
          </p>
          <div className="relative h-[3px] bg-white/10 overflow-hidden mb-6">
            <div
              className="absolute inset-y-0 left-0 bg-[#FF3D2E] transition-[width] duration-1000 ease-out"
              style={{ width: `${Math.min(96, (elapsedSec / 60) * 100)}%` }}
            />
          </div>
          <ul className="space-y-2.5">
            {PROGRESS_STEPS.map((step, i) => {
              const done = i < progressIdx;
              const active = i === progressIdx;
              return (
                <li key={step} className="flex items-center gap-3 text-sm">
                  <span
                    className={`w-6 h-6 flex items-center justify-center border text-[10px] tracking-[0.15em] ${
                      done
                        ? 'bg-[#FF3D2E] border-[#FF3D2E] text-white'
                        : active
                        ? 'border-white/60 text-white animate-pulse'
                        : 'border-white/15 text-white/30'
                    }`}
                  >
                    {done ? '✓' : String(i + 1).padStart(2, '0')}
                  </span>
                  <span className={done ? 'text-white/50 line-through' : active ? 'text-white' : 'text-white/35'}>
                    {step}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {result && (
        <div
          className="relative border border-white/20 bg-black/60 backdrop-blur p-6 md:p-8 animate-[fadeUp_500ms_ease-out]"
          data-testid="brand-preview-result"
        >
          <button
            onClick={reset}
            aria-label="close preview"
            className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center border border-white/20 hover:border-white/60 text-white/60 hover:text-white transition-colors z-10"
            data-testid="brand-preview-reset"
          >
            <X className="w-3.5 h-3.5" />
          </button>

          <p className="text-[10px] tracking-[0.35em] text-[#FF3D2E] mb-2">— SMG PREVIEW FOR</p>
          <h3 className="font-display text-3xl md:text-4xl text-white tracking-tight mb-1">
            {result.brand}
          </h3>
          <p className="text-[10px] tracking-[0.3em] text-white/55 mb-6">{result.category}</p>

          {/* Bespoke visual mockup */}
          <div className="relative aspect-[16/9] overflow-hidden mb-6 border border-white/10 bg-neutral-900" data-testid="brand-preview-visual">
            {visual ? (
              <>
                <img src={visual} alt="" draggable={false}
                  className="absolute inset-0 w-full h-full object-cover animate-[fadeUp_500ms_ease-out]" />
                <div className="absolute inset-0"
                  style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.05) 45%, rgba(0,0,0,0.7) 100%)' }} />
                {/* Cinematic corner brackets */}
                <span className="absolute top-3 left-3 w-6 h-6 border-l border-t border-white/60" />
                <span className="absolute top-3 right-3 w-6 h-6 border-r border-t border-white/60" />
                <span className="absolute bottom-3 left-3 w-6 h-6 border-l border-b border-white/60" />
                <span className="absolute bottom-3 right-3 w-6 h-6 border-r border-b border-white/60" />
                {/* Tagline stamped in corner */}
                <div className="absolute bottom-4 left-5 right-5 flex items-end justify-between gap-4">
                  <p className="font-display text-white text-lg md:text-2xl leading-tight tracking-tight max-w-[75%]"
                    style={{ textShadow: '0 4px 20px rgba(0,0,0,0.75)' }}>
                    {result.tagline}
                  </p>
                  <p className="text-[9px] tracking-[0.4em] text-white/75">SMG · MOCK</p>
                </div>
              </>
            ) : (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center">
                  <Loader2 className="w-5 h-5 animate-spin text-[#FF3D2E] mx-auto mb-3" />
                  <p className="text-[10px] tracking-[0.35em] text-white/50">
                    {visualLoading ? 'RENDERING CAMPAIGN VISUAL…' : 'VISUAL UNAVAILABLE'}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Thesis (typewriter) */}
          <p className="text-white/90 text-base md:text-lg leading-relaxed mb-8 max-w-3xl min-h-[3.5rem]">
            {typedThesis}
            {typedThesis.length < (result.thesis?.length || 0) && (
              <span className="inline-block w-2 h-[1em] bg-[#FF3D2E] ml-0.5 align-text-bottom animate-pulse" />
            )}
          </p>

          {/* Dominance scores */}
          <div className="mb-8 border-t border-white/10 pt-6" data-testid="brand-preview-scores">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="w-4 h-4 text-[#FF3D2E]" />
              <p className="text-[10px] tracking-[0.35em] text-white/55">CATEGORY DOMINANCE INDEX</p>
            </div>
            <div className="flex items-end gap-8">
              <ScoreGauge label="TODAY" value={result.dominance_score_current} color="#F4EFE4" delay={200} />
              <ScoreGauge label="12–18 MONTHS WITH SMG" value={result.dominance_score_projected} color="#FF3D2E" delay={700} />
            </div>
          </div>

          <p className="text-[10px] tracking-[0.3em] text-white/55 mb-4">— FIRST FOUR MOVES</p>
          <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-5 mb-8">
            {result.moves?.map((m, i) => (
              <li key={i} className="border-t border-white/10 pt-3 flex items-start gap-3">
                <span className="font-display text-lg text-white/30 leading-none pt-1">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <div>
                  <h4 className="text-white text-sm md:text-base font-medium tracking-tight mb-1">{m.title}</h4>
                  <p className="text-white/60 text-[13px] leading-relaxed">{m.desc}</p>
                </div>
              </li>
            ))}
          </ul>

          <div className="grid sm:grid-cols-2 gap-6 border-t border-white/10 pt-6 mb-8">
            <div>
              <p className="text-[10px] tracking-[0.3em] text-white/55 mb-2">— REVENUE LIFT</p>
              <p className="text-white text-[15px] leading-relaxed">{result.revenue_lift}</p>
            </div>
            <div>
              <p className="text-[10px] tracking-[0.3em] text-white/55 mb-2">— TAGLINE</p>
              <p className="font-script text-2xl md:text-3xl text-[#FF3D2E] leading-tight">
                {result.tagline}
              </p>
            </div>
          </div>

          {/* "See a real one" cross-link */}
          {related && (
            <Link
              to={`/work/${result.related_case_slug}`}
              className="block border border-white/10 hover:border-[#FF3D2E]/60 bg-white/[0.02] p-4 mb-6 transition-colors group"
              data-testid="brand-preview-related"
            >
              <div className="flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <p className="text-[10px] tracking-[0.3em] text-white/50 mb-1">— WE&apos;VE BUILT THIS BEFORE</p>
                  <p className="text-white text-base md:text-lg tracking-tight truncate">See how we did it for {related.title}</p>
                  <p className="text-[11px] tracking-[0.25em] text-white/45 mt-1">{related.tag}</p>
                </div>
                <ArrowUpRight className="w-4 h-4 text-white/60 group-hover:text-[#FF3D2E] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all shrink-0" />
              </div>
            </Link>
          )}

          <div className="flex flex-wrap items-center gap-3">
            <a
              href="#contact"
              className="inline-flex items-center gap-2 px-6 py-4 bg-[#FF3D2E] hover:bg-[#ff5446] text-white text-[11px] tracking-[0.25em] transition-colors"
              data-testid="brand-preview-cta"
            >
              BUILD THIS WITH SMG
              <ArrowRight className="w-3.5 h-3.5" />
            </a>
            <button
              onClick={reset}
              className="inline-flex items-center gap-2 px-6 py-4 border border-white/25 hover:border-white/60 text-white text-[11px] tracking-[0.25em] transition-colors"
              data-testid="brand-preview-try-another"
            >
              TRY ANOTHER BRAND
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
